import React from 'react';
import PropTypes from 'prop-types';

const CalendarHeader = ({
  currentMonth,
  weekDates,
  onPrevMonth,
  onNextMonth,
  onToday,
  viewMode,
  setViewMode,
  filters,
  setFilters,
  onNewPost
}) => {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Format week range display
  const getWeekRangeDisplay = () => {
    if (!weekDates || weekDates.length === 0) {
      return `${months[currentMonth.getMonth()]} ${currentMonth.getFullYear()}`;
    }
    
    const firstDay = weekDates[0];
    const lastDay = weekDates[weekDates.length - 1];
    
    const firstMonth = months[firstDay.getMonth()];
    const lastMonth = months[lastDay.getMonth()];
    const firstYear = firstDay.getFullYear();
    const lastYear = lastDay.getFullYear();
    
    // If spanning different months, show both months
    if (firstMonth !== lastMonth) {
      if (firstYear === lastYear) {
        return `${firstMonth} - ${lastMonth} ${firstYear}`;
      } else {
        return `${firstMonth} ${firstYear} - ${lastMonth} ${lastYear}`;
      }
    }
    // Same month, just show month and year
    return `${firstMonth} ${firstYear}`;
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
      {/* Top Row: Channel Selector, Month Nav, View Switcher, New Post */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        {/* Left: Channel Dropdown */}
        <select
          value={filters.channel}
          onChange={(e) => setFilters({ ...filters, channel: e.target.value })}
          className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <option value="all">All Channels</option>
          <option value="Facebook">Facebook</option>
          <option value="Instagram">Instagram</option>
          <option value="LinkedIn">LinkedIn</option>
          <option value="Twitter">Twitter</option>
        </select>

        {/* Center: Week Navigation */}
        <div className="flex items-center gap-3">
          <button
            onClick={onPrevMonth}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Previous week"
          >
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h2 className="text-lg font-semibold text-gray-900 min-w-[200px] text-center">
            {getWeekRangeDisplay()}
          </h2>
          <button
            onClick={onNextMonth}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Next week"
          >
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          <button
            onClick={onToday}
            className="px-3 py-2 text-sm font-medium text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
          >
            Today
          </button>
        </div>

        {/* View Switcher */}
        <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setViewMode('list')}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              viewMode === 'list'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            List
          </button>
          <button
            onClick={() => setViewMode('calendar')}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              viewMode === 'calendar'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Calendar
          </button>
          <button
            onClick={() => setViewMode('ai_schedules')}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              viewMode === 'ai_schedules'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            ✨ AI Schedules
          </button>
        </div>

        {/* Right: New Post Button */}
        <button
          onClick={onNewPost}
          className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors shadow-sm flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          New Post
        </button>
      </div>

      {/* Bottom Row: Filters and Timezone */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Post Status Filter */}
        <select
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          className="px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-sm hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <option value="all">All Posts</option>
          <option value="draft">Draft</option>
          <option value="scheduled">Scheduled</option>
          <option value="published">Published</option>
          <option value="failed">Failed</option>
        </select>

        {/* Channels Filter (duplicate for quick access) */}
        <select
          value={filters.channel}
          onChange={(e) => setFilters({ ...filters, channel: e.target.value })}
          className="px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-sm hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <option value="all">All Channels</option>
          <option value="Facebook">Facebook</option>
          <option value="Instagram">Instagram</option>
          <option value="LinkedIn">LinkedIn</option>
          <option value="Twitter">Twitter</option>
        </select>

        {/* Tags Filter */}
        <select
          value={filters.tags}
          onChange={(e) => setFilters({ ...filters, tags: e.target.value })}
          className="px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-sm hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <option value="all">All Tags</option>
          <option value="promotion">Promotion</option>
          <option value="announcement">Announcement</option>
          <option value="engagement">Engagement</option>
          <option value="content">Content</option>
        </select>

        {/* Show Holidays Toggle */}
        <label className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-sm hover:border-gray-400 cursor-pointer transition-colors">
          <input
            type="checkbox"
            checked={filters.showHolidays}
            onChange={(e) => setFilters({ ...filters, showHolidays: e.target.checked })}
            className="w-4 h-4 text-indigo-600 rounded focus:ring-2 focus:ring-indigo-500 cursor-pointer"
          />
          <span className="flex items-center gap-1.5 font-medium text-gray-700">
            <span>🇮🇳</span>
            Indian Holidays
          </span>
        </label>

        {/* Timezone */}
        <div className="ml-auto flex items-center gap-2 text-sm text-gray-600">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <select
            value={filters.timezone}
            onChange={(e) => setFilters({ ...filters, timezone: e.target.value })}
            className="px-2 py-1 rounded border border-gray-300 bg-white text-sm hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
            <option value="America/New_York">America/New York (EST)</option>
            <option value="Europe/London">Europe/London (GMT)</option>
            <option value="Asia/Tokyo">Asia/Tokyo (JST)</option>
          </select>
        </div>
      </div>
    </div>
  );
};

CalendarHeader.propTypes = {
  currentMonth: PropTypes.instanceOf(Date).isRequired,
  weekDates: PropTypes.array,
  onPrevMonth: PropTypes.func.isRequired,
  onNextMonth: PropTypes.func.isRequired,
  onToday: PropTypes.func.isRequired,
  viewMode: PropTypes.string.isRequired,
  setViewMode: PropTypes.func.isRequired,
  filters: PropTypes.shape({
    channel: PropTypes.string,
    status: PropTypes.string,
    tags: PropTypes.string,
    timezone: PropTypes.string,
    showHolidays: PropTypes.bool
  }).isRequired,
  setFilters: PropTypes.func.isRequired,
  onNewPost: PropTypes.func.isRequired
};

export default CalendarHeader;
